import { Outlet, createBrowserRouter } from 'react-router-dom';
import { StudyPageCommunity } from './pages/StudyPageCommunity';
import { ShareCollectionRoute, ShareStudyRoute } from './share/ShareRoute';
import { KitPage } from './dev/KitPage';
import { installFixtures } from './dev/fixtures';
import { CollectionsPage } from './pages/CollectionsPage';
import { ExplorePage } from './pages/ExplorePage';
import { HomePage } from './pages/HomePage';
import { RootRoute } from './pages/RootRoute';
import { CollectionRoute } from './pages/CollectionRoute';
import { UploadStudyPage } from './upload/UploadStudyPage';
import { UploadFilesPage } from './upload/UploadFilesPage';
import { ImportingPage } from './upload/ImportingPage';
import { StudyDetailsPage } from './details/StudyDetailsPage';
import { CommunityLayout } from './layout/AppLayout';
import { UploadShell } from './layout/UploadShell';
import { AuthPage } from './auth/AuthPage';
import { WorkspacePage } from './pages/WorkspacePage';
import { AccountPage } from './pages/AccountPage';
import { WorkspaceActionsPage } from '../features/actions/WorkspaceActionsPage';
import { enterCommunityShell } from './shell';
import { applyCommunityQueryDefaults } from './api/freshness';
import { queryClient } from '../hooks/queryClient';

const basename = import.meta.env.BASE_URL.replace(/\/+$/, '') || '/';


/** The community shell's routes (IMPLEMENTATION_PLAN §2.3). */
export function buildCommunityRouter() {
  enterCommunityShell();
  applyCommunityQueryDefaults(queryClient);
  if (import.meta.env.DEV && /\/dev(\/|$)/.test(window.location.pathname)) installFixtures();
  return createBrowserRouter(
    [
      {
        // Above every route: uploads keep running wherever the person goes.
        element: (
          <>
            <Outlet />
            <UploadShell />
          </>
        ),
        children: [
      {
        element: <CommunityLayout />,
        children: [
          { path: '/', element: <RootRoute /> },
          // A share link to a collection: its page, in the shell like any
          // collection page. What opens inside it is the route below.
          { path: '/s/col/:code', element: <ShareCollectionRoute /> },
          { path: '/explore', element: <ExplorePage /> },
          { path: '/collections', element: <CollectionsPage /> },
          { path: '/collections/:publicId', element: <CollectionRoute /> },
          { path: '/collections/:publicId/upload', element: <UploadStudyPage /> },
          { path: '/collections/:publicId/upload/files', element: <UploadFilesPage /> },
          { path: '/collections/:publicId/import/:studyId', element: <ImportingPage /> },
          // A study's details, edited after it is published (the viewer itself
          // is outside the shell, at /studies/:studyId).
          { path: '/studies/:studyId/details', element: <StudyDetailsPage /> },
          { path: '/workspace', element: <WorkspacePage /> },
          { path: '/account', element: <AccountPage /> },
          { path: '/account/:tab', element: <AccountPage /> },
          // The actions the selected workspace runs (Manage actions, in the workspace menu).
          { path: '/actions', element: <WorkspaceActionsPage /> },
          ...(import.meta.env.DEV
            ? [
                { path: '/dev/kit', element: <KitPage /> },
                // The same pages, over the fake API (fixtures.ts). ?guest=1
                // (sticky for the tab) makes the fake session answer 401.
                { path: '/dev', element: <HomePage /> },
                { path: '/dev/explore', element: <ExplorePage /> },
                { path: '/dev/collections', element: <CollectionsPage /> },
                { path: '/dev/collections/:publicId', element: <CollectionRoute /> },
                { path: '/dev/collections/:publicId/upload', element: <UploadStudyPage /> },
                { path: '/dev/collections/:publicId/upload/files', element: <UploadFilesPage /> },
                { path: '/dev/collections/:publicId/import/:studyId', element: <ImportingPage /> },
                { path: '/dev/studies/:studyId/details', element: <StudyDetailsPage /> },
                { path: '/dev/workspace', element: <WorkspacePage /> },
                { path: '/dev/account', element: <AccountPage /> },
                { path: '/dev/account/:tab', element: <AccountPage /> },
                { path: '/dev/actions', element: <WorkspaceActionsPage /> },
              ]
            : []),
        ],
      },
      // The auth screens (community/auth/AuthPage.tsx). /verify, /reset and
      // /invite are also where emailed links land.
      { path: '/signin', element: <AuthPage screen="signin" /> },
      { path: '/signup', element: <AuthPage screen="signup" /> },
      { path: '/forgot', element: <AuthPage screen="forgot" /> },
      { path: '/verify', element: <AuthPage screen="verify" /> },
      { path: '/reset', element: <AuthPage screen="reset" /> },
      { path: '/setup', element: <AuthPage screen="setup" /> },
      { path: '/invite', element: <AuthPage screen="invite" /> },
      ...(import.meta.env.DEV
        ? (['signin', 'signup', 'forgot', 'verify', 'reset', 'setup', 'invite'] as const).map((s) => ({ path: `/dev/${s}`, element: <AuthPage screen={s} /> }))
        : []),
      // By public id (`stu_…`): a StudyInstanceUID names a study only inside
      // its workspace, so it cannot address one from anywhere in the app.
      { path: '/studies/:studyId', element: <StudyPageCommunity /> },
      ...(import.meta.env.DEV ? [{ path: '/dev/studies/:studyId', element: <StudyPageCommunity /> }] : []),
      // A share link to a study (/s/stu/<code>[/<slide>]), and a study opened
      // inside a shared collection (/s/col/<code>/<study>[/<slide>]): the
      // viewer, outside the shell like /studies/:studyId.
      { path: '/s/:seg/:code/*', element: <ShareStudyRoute /> },
        ],
      },
    ],
    { basename },
  );
}
